<template>
  <div
    id="product-form"
    class="product-form"
    :class="{ [$class('loading')]: loading }"
    data-sign-post-location="main"
    js-sign-post
  >
    <form
      class="product-form__form"
      @submit.prevent="handleAddToCart"
    >
      <product-options
        v-show="showProductOptions"
        class="product-form__options"
        :color-options="colorOptions"
        :filtered-media-image="filteredMediaImage"
        :is-one-size="isOneSize"
        :is-size-guide-expanded="isSizeGuideExpanded"
        :loading="loading"
        :namespace="namespace"
        :product="product"
        :section="section"
        :has-size-guide="hasSizeGuide"
        :variant="variant"
        @open-size-guide="handleOpenSizeGuide"
        @update-active-swatch="handleActiveSwatchUpdate"
        @update-variant="handleUpdateVariant"
      />

      <div
        v-if="isMobile && !isSticky"
        class="product-form__payment-options"
      >
        <loyalty-points-messaging
          :loyalty-copy-info="loyaltyCopyInfo"
          namespace="product-form-mobile"
          :price="variant?.price?.amount || product?.price?.amount"
        />

        <klarna-messaging
          class="product-form__klarna"
          namespace="product-form-mobile"
          :price="variant?.price?.amount"
          :variant="variant"
        />

        <span
          class="text-body-s-regular"
          v-text="$string('product.payment_options.or')"
        />

        <affirm-messaging
          namespace="product-form-mobile"
          page-type="product"
          :price="variant?.price?.amount || product?.price?.amount"
          :variant="variant"
        />

        <sr-benefits />
      </div>

      <input
        name="quantity"
        readonly
        type="hidden"
        :value="quantity"
      >

      <loyalty-points-messaging
        v-if="!isMobile && !isSticky"
        :loyalty-copy-info="loyaltyCopyInfo"
        namespace="product-form-desktop"
        :price="variant?.price?.amount || product?.price?.amount"
      />
      <template v-if="product.giftCard">
        <btn
          class="
            Rise-add-to-cart-button
            visually-hidden
          "
          :label="addToCartText"
        />

        <btn
          class="
            product-form__add-to-cart
            GiftWizard-gift-button
            gwbutton"
          :label="addToCartText"
          :modifiers="ctaModifiers"
          :type="ctaType"
          :disabled="disableSendGiftCardButton"
        >
          <template
            #hoverIcon
          >
            <icon-circle />
          </template>
        </btn>
      </template>

      <div
        v-if="showAddToCartButton"
        class="product-form__footer col xs4"
        :class="{ 'product-form__footer--border': isLowStock }"
      >
        <p
          v-if="isLowStock"
          class="product-form__low-stock-message"
          v-text="$string('product.form.low_stock_warning')"
        />

        <btn
          :aria-controls="isOutOfStock ? 'notify-me-drawer' : null"
          :aria-expanded="isOutOfStock ? outOfStockExpanded : null"
          :class="{ 'product-form__add-to-cart': !isOutOfStock }"
          :label="addToCartText"
          :modifiers="ctaModifiers"
          :type="ctaType"
          @click.prevent="handleAddToCart"
        >
          <template
            v-if="!isOutOfStock"
            #hoverIcon
          >
            <icon-circle />
          </template>
        </btn>

        <span
          v-if="preorderDate"
          class="product-form__preorder-message text-body-m-regular"
          v-text="$string('product.form.preorder_date_string', {
            replace: { date: preorderDate }
          })"
        />

        <div
          v-if="!isMobile && !isSticky"
          class="product-form__payment-options"
        >
          <klarna-messaging
            class="product-form__klarna"
            namespace="product-form"
            :price="variant?.price?.amount"
            :variant="variant"
          />

          <span
            class="text-body-s-regular"
            v-text="$string('product.payment_options.or')"
          />

          <affirm-messaging
            namespace="product-form"
            page-type="product"
            :price="variant?.price?.amount || product?.price?.amount"
            :variant="variant"
          />

          <sr-benefits />
        </div>
      </div>
    </form>
  </div>
</template>

<script>

/**
 * Vue: Product form (product-form)
 * -----------------------------------------------------------------------------
 * Product form to handle variant selection and adding to cart.
 *
 * @param {Array} colorOptions - All color options for all matched products.
 * @param {Object} filteredMediaImage - First image in media array.
 * @param {Boolean} isOneSize - If the product only has one size option.
 * @param {String|Boolean} preorderDate - Preorder date.
 * @param {Boolean} hasSizeGuide - Whether Liquid passed size guide data.
 * @param {Boolean} isSizeGuideExpanded - If size guide drawer is expanded.
 * @param {Boolean} [isSticky] - If instantiation is sticky.
 * @param {Boolean} [loading] - Loading state.
 * @param {String} [loyaltyCopyInfo] - Fallback if item doesn't have a
 * price.
 * @param {String} namespace - Form namespace.
 * @param {Boolean} outOfStockExpanded - If the notify me drawer is open.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object} section - Main product section settings.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 *
 * @emits updateVariant - Emit new variant object.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import AffirmMessaging from '~async/affirm-messaging/affirm-messaging'
import KlarnaMessaging from '~async/klarna-messaging/klarna-messaging'
import LoyaltyPointsMessaging from '~async/loyalty-points-messaging/loyalty-points-messaging'
import ProductOptions from '~async/product-form/product-options'
import Btn from '~global/btn/btn'

import IconCircle from '~icons/scribbles/circle.svg'

import { isColorCodeInStr } from '~/helpers/product'
import { emitUpdateVariant } from '~/helpers/vue'

export default {
  name: 'ProductForm',

  components: {
    AffirmMessaging,
    Btn,
    IconCircle,
    KlarnaMessaging,
    LoyaltyPointsMessaging,
    ProductOptions,
  },

  props: {
    colorOptions: {
      type: Array,
      default: () => [],
    },
    filteredMediaImage: {
      type: Object,
      default: () => ({}),
    },
    isOneSize: {
      type: Boolean,
      default: false,
    },
    preorderDate: {
      type: [Boolean, String],
      default: false,
    },
    hasSizeGuide: {
      type: Boolean,
      default: false,
    },
    isSizeGuideExpanded: {
      type: Boolean,
      default: false,
    },
    isSticky: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    loyaltyCopyInfo: {
      type: String,
      default: '',
    },
    outOfStockExpanded: {
      type: Boolean,
      default: false,
    },
    namespace: {
      type: String,
      default: '',
      required: true,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    section: {
      type: Object,
      default: () => ({
        autoOpenCartDrawer: true,
        displayRecipientForm: true,
        hideGiftCardOptionNames: true,
        id: '',
        resetRecipientFormOnAdd: true,
      }),
      required: true,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
  },

  emits: {
    'open-notify-me-drawer': (payload) => typeof payload === 'boolean',
    'open-size-guide': (payload) => typeof payload === 'boolean',
    'update-active-swatch': (payload) => typeof payload === 'object',
    'update-variant': emitUpdateVariant,
  },

  data() {
    return {
      activeSwatch: {},
      error: '',
      giftCard: {
        enableForm: false,
        fields: {
          Message: '',
          'Recipient email': '',
          'Recipient name': '',
          'Send on': '',
        },
        timezoneOffset: '',
      },
      lineItemProperties: {},
      lowStockThreshold: this.$variable('settings.product.lowStockThreshold'),
      submission: {
        hasAttemptedSubmission: false,
        addedVariants: [],
      },
      quantity: 1,
      state: 'ready',
      timeout: {},
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      if (this.product.giftCard) {
        return this.variant
          ? this.$string('product.cta.send_gift_card')
          : this.$string('product.cta.select_amount')
      }

      if (!this.variant && this.submission.hasAttemptedSubmission) {
        return this.$string('product.cta.select_size')
      }

      if (this.isOutOfStock) {
        return this.$string('product.cta.sold_out')
      }

      if (this.preorderDate) {
        return this.$string('product.cta.preorder')
      }

      const useAddMoreText = this.isMobile
        ? this.submission.addedVariants.length > 0
        : this.submission.addedVariants.includes(this.variant.id)

      return useAddMoreText
        ? this.$string('product.cta.add_more_to_cart')
        : this.$string('product.cta.add_to_cart')
    },

    /**
     * Return CTA modifiers based on stock.
     * @returns {String}
     */
    ctaModifiers() {
      return this.isOutOfStock
        ? 'add-to-cart, block, center, secondary'
        : 'add-to-cart, block, center'
    },

    /**
     * Return button type based on variant availability.
     * @returns {String}
     */
    ctaType() {
      return this.isOutOfStock ? 'button' : 'submit'
    },

    /**
     * Whether to disable gift card button.
     * @returns {Boolean}
     */
    disableSendGiftCardButton() {
      if (!this.product.giftCard) {
        return false
      }

      if (!this.variant) {
        return true
      }

      return false
    },

    /**
     * Return if the selected variant is low stock and should display the low
     * stock warning.
     * @returns {Boolean}
     */
    isLowStock() {
      return this.variant &&
        this.variant.inventory < this.lowStockThreshold &&
        this.variant.inventory > 0
    },

    /**
     * Return if current variant is out of stock.
     * @returns {Boolean}
     */
    isOutOfStock() {
      return this.variant && !this.variant.available
    },

    /**
     * Compute whether to render the add to cart button.
     * @returns {Boolean}
     */
    showAddToCartButton() {
      if (this.product.giftCard) {
        return false
      }

      if (this.isMobile) {
        return this.isSticky
      }

      return true
    },

    /**
     * Compute whether to show product options.
     * @returns {Boolean}
     */
    showProductOptions() {
      if (this.isSticky && this.isMobile) {
        return false
      }

      return !this.product.hasOnlyDefaultVariant
    },

  },

  watch: {

    /**
     * Watch product to reset quantity and error.
     */
    product() {
      this.quantity = 1
      this.error = ''
    },

    /**
     * Watch variant to reset error.
     */
    variant() {
      this.error = ''
    },
  },

  beforeMount() {
    this.giftCard.timezoneOffset = new Date().getTimezoneOffset().toString()
  },

  mounted() {
    this.setInitialActiveSwatch()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      openOverlay: 'overlays/open',
    }),

    /**
     * Handle add to cart submission.
     */
    async handleAddToCart() {
      if (!this.variant) {
        this.submission.hasAttemptedSubmission = true
        this.error = this.$string('product.form.select_variant')
        return
      }

      if (this.isOutOfStock) {
        this.handleOpenNotifyMeDrawer()
        return
      }

      clearTimeout(this.timeout)
      this.error = ''
      this.state = 'adding'

      if (this.$variable('storybook')) {
        this.storybookAddToCart()
        return
      }

      const items = this.buildCartItem()

      try {
        await this.addItem(items)
        this.setProductAddedToCart()
        await this.resetState()

      } catch (error) {
        this.state = 'error'

        if (error.description) {
          this.handleErrorMessage(error.description)
          return
        }

        cnvs.ReportError('Failed to add product to cart', error)
      }
    },

    /**
     * Built cart item array.
     * @returns {Array}
     */
    buildCartItem() {
      return [
        {
          id: this.variant.id,
          properties: this.product.giftCard
            ? {
              ...this.giftCardLineItemProperties,
              ...this.lineItemProperties,
            }
            : this.lineItemProperties,
          quantity: this.quantity,
        },
      ]
    },

    /**
     * Set appropriate variables when a product has successfully been added to
     * the cart.
     */
    setProductAddedToCart() {
      this.state = 'added'
      this.submission.addedVariants.push(this.variant.id)

      if (
        this.section.autoOpenCartDrawer &&
        this.getComponentExists('cart-drawer')
      ) {
        this.openCartDrawer()
      }
    },

    /**
     * Open cart drawer overlay.
     */
    openCartDrawer() {
      const overlay = this.isMobile
        ? {
          component: 'cart-drawer-mobile',
          ignoreDismissed: true,
          namespace: 'cartDrawerMobile',
        }
        : {
          component: 'cart-drawer',
          ignoreDismissed: true,
          namespace: 'cartDrawer',
        }

      if (document.querySelector('.Rise-popup.open')) {
        setTimeout(() => {
          this.openOverlay(overlay)
        }, '3000')
      } else {
        this.openOverlay(overlay)
      }
    },

    /**
     * Reset product form state after adding to cart after message delay.
     */
    async resetState() {
      await new Promise((resolve) => {
        setTimeout(resolve, this.$timing('message'))
      })

      this.state = 'ready'

      /**
       * Reset gift card recipient form based on setting.
       */
      if (this.product.giftCard && this.section.resetRecipientFormOnAdd) {
        this.giftCard = {
          enableForm: false,
          fields: {
            Message: '',
            'Recipient email': '',
            'Recipient name': '',
            'Send on': '',
          },
        }
      }

      this.quantity = 1
    },

    /**
     * Handle update variant event.
     * @param {Object} variant - Emitted variant object.
     */
    handleUpdateVariant(variant) {
      this.$emit('update-variant', variant)
    },

    /**
     * Handle update active code event.
     * @param {String} colorData - Emitted color code.
     */
    handleActiveSwatchUpdate(colorData) {
      this.$emit('update-active-swatch', colorData)
    },

    /**
     * Handle emit event to open the notify me drawer.
     */
    handleOpenNotifyMeDrawer() {
      this.$emit('open-notify-me-drawer', true)
    },

    /**
     * Handle update recipient form event.
     * @param {Object} recipientForm - Emitted recipient form.
     */
    handleUpdateRecipientForm(recipientForm) {
      this.giftCard = recipientForm
    },

    /**
     * Handle error message.
     * @param {Object} errorDescription - Error description from add request.
     */
    handleErrorMessage(errorDescription) {
      if (typeof error === 'string') {
        this.error = errorDescription
        return
      }

      this.error = Object.values(errorDescription)
        .flat()
        .map((error) => {
          return error.endsWith('.') ? error : `${error}.`
        })
        .join('<br>')
    },

    /**
     * Set the initial active swatch based on the featured image.
     */
    setInitialActiveSwatch() {
      const colorOptions = this.product.colorOptions[0]

      if (!colorOptions) {
        this.activeSwatch = {}
        return
      }

      const colorIndex = colorOptions.colorCodes.findIndex((code) => {
        return isColorCodeInStr(code, this.product.featuredImage.src)
      })

      this.activeSwatch = {
        code: colorOptions.colorCodes[colorIndex],
        label: colorOptions.colorLabels[colorIndex],
        value: colorOptions.values[colorIndex],
      }

      this.handleActiveSwatchUpdate(this.activeSwatch)
    },

    /**
     * Helper function for storybook add to cart event.
     */
    storybookAddToCart() {
      if (!this.variant.available) {
        this.error = this.$string('product.form.sold_out')
        this.state = 'ready'
        return
      }

      this.timeout = setTimeout(() => {
        this.state = 'added'
      }, this.$timing('slow'))

      this.timeout = setTimeout(() => {
        this.state = 'ready'
      }, this.$timing('message'))
    },

    /**
     * Emit event to open the size guide drawer.
     * @param {Boolean} payload - payload from product options.
     */
    handleOpenSizeGuide(payload) {
      this.$emit('open-size-guide', payload)
    },
  },
}
</script>

<style lang="scss">
@import './product-form';
</style>
